import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Smartphone, Truck } from "lucide-react";
import { api } from "@/lib/api";

export default function Pricing() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const navigate = useNavigate();
  const [note, setNote] = useState("OLED = Original-Qualität, LCD = günstigere Lösung. Alle Preise inkl. Einbau & Garantie, zzgl. einmalig 6,95 € Versand (deutschlandweit).");
  const [taxNote, setTaxNote] = useState("");

  useEffect(() => {
    api.get("/pricing").then((r) => setRows(r.data)).catch(() => {});
    api.get("/service-types").then((r) => setServices(r.data.filter((s) => s.active))).catch(() => {});
    api.get("/content").then((r) => r.data?.pricing_note && setNote(r.data.pricing_note)).catch(() => {});
    api.get("/company").then((r) => {
      const c = r.data || {};
      setTaxNote(c.kleinunternehmer
        ? "Als Kleinunternehmer gemäß § 19 UStG weisen wir keine Umsatzsteuer aus."
        : `Alle Preise inkl. ${c.tax_rate || 19}% gesetzlicher MwSt.`);
    }).catch(() => {});
  }, []);

  const gridTemplate = { gridTemplateColumns: `1.6fr 1fr 1fr ${services.map(() => "1fr").join(" ")}` };

  return (
    <section id="preise" data-testid="pricing-section" className="relative z-10 bg-[#050505] py-24 lg:py-36 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#888] mb-4">Preisliste</p>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-2xl">
              Faire Festpreise<br />für jedes Modell.
            </h1>
          </div>
          <p className="text-[#999] max-w-sm leading-relaxed flex items-start gap-2">
            <Truck size={18} className="mt-0.5 shrink-0" />
            {note}
          </p>
        </div>

        <div className="overflow-x-auto rounded-3xl border border-white/10 bg-[#0a0a0a]">
          <div className="min-w-[640px]">
            <div className="grid gap-3 px-6 lg:px-8 py-5 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-[#888]" style={gridTemplate}>
              <span>Modell</span>
              <span className="text-right flex items-center justify-end gap-2"><Smartphone size={13} /> OLED</span>
              <span className="text-right">Display LCD</span>
              {services.map((s) => <span key={s.id} className="text-right whitespace-nowrap">{s.name}</span>)}
            </div>

            {rows.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 8) * 0.03 }}
                className="grid gap-3 px-6 lg:px-8 py-5 border-b border-white/5 hover:bg-white/[0.02] transition-colors items-center"
                style={gridTemplate}
                data-testid={`pricing-public-${r.id}`}
              >
                <span className="text-white text-sm lg:text-base">{r.model}</span>
                <span className="text-right font-mono text-white text-sm lg:text-base">
                  {r.oled_available === false ? "—" : (
                    <>
                      {r.oled_original_price > r.oled_price && r.oled_price > 0 && <span className="block text-xs text-[#666] line-through">{r.oled_original_price.toFixed(0)} €</span>}
                      {r.oled_price.toFixed(0)} €
                    </>
                  )}
                </span>
                <span className="text-right font-mono text-white text-sm lg:text-base">
                  {r.lcd_original_price > r.lcd_price && r.lcd_price > 0 && <span className="block text-xs text-[#666] line-through">{r.lcd_original_price.toFixed(0)} €</span>}
                  {r.lcd_price.toFixed(0)} €
                </span>
                {services.map((s) => {
                  const entry = r.service_prices?.[s.key] || {};
                  return (
                    <span key={s.id} className="text-right font-mono text-white text-sm lg:text-base">
                      {entry.original_price > entry.sell_price && entry.sell_price > 0 && <span className="block text-xs text-[#666] line-through">{entry.original_price.toFixed(0)} €</span>}
                      {(entry.sell_price || 0).toFixed(0)} €
                    </span>
                  );
                })}
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <button
            onClick={() => navigate("/beauftragen", { state: { preselect: "Display-Reparatur" } })}
            data-testid="pricing-cta"
            className="rounded-full bg-white text-[#050505] px-8 py-4 font-medium hover:bg-[#d1d5db] transition-colors duration-300"
          >
            Jetzt Reparatur beauftragen
          </button>
        </div>

        {taxNote && (
          <p data-testid="pricing-tax-note" className="mt-6 text-center font-mono text-xs text-[#777]">{taxNote}</p>
        )}
      </div>
    </section>
  );
}
